/**
 * La marca del sitio: sus colores y sus letras, en un solo sitio.
 *
 * Estaban únicamente en el editor, dentro del tema de cada página. Eso los dejaba
 * fuera del alcance de todo lo que no es una página del editor —la página de
 * reserva, los correos, los documentos legales—, y por eso la página de reserva
 * acabó con su propio selector de color: no había de dónde heredar.
 *
 * Aquí se edita el MISMO dato que edita el editor, con la MISMA derivación
 * (`@webbuilder/theme`). No es una copia ni una vista aparte: es la otra puerta
 * a lo mismo. Por eso los colores se aplican con `applyColorsToTheme` en vez de
 * escribirse a mano en `theme.colors`: los valores se hornean dentro de cada rol
 * —el título guarda su color, no una referencia—, así que guardar la paleta sin
 * pasar por ahí no repintaría absolutamente nada.
 */
import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../lib/i18n'
import {
  COLOR_TOKENS,
  DEFAULT_PAGE_THEME,
  applyColorsToTheme,
  applyFontsToTheme,
  normalizeProjectTheme,
  paletteColorValues,
  paletteSampler,
  themeColorsToArray,
  type ProjectTheme,
} from '@webbuilder/theme'
import { IconArrowLeft, IconCheck, IconLoader2, IconPaint, IconPhotoPlus, IconTypography } from '@tabler/icons-react'
import { getProjectTheme, saveProjectTheme } from '../../../lib/api'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { Notice } from '../Notice'

/**
 * Las tipografías que ofrece el editor en sus combinaciones.
 *
 * Se listan por nombre y se guardan con su familia de respaldo, que es la forma
 * en que el tema las espera: `'Inter', sans-serif`. Escribir sólo el nombre deja
 * la página sin alternativa si la fuente no carga.
 */
const FONTS = [
  'Inter', 'Archivo', 'Figtree', 'Manrope', 'Karla', 'Urbanist',
  'Plus Jakarta Sans', 'DM Sans', 'Sora', 'Outfit',
] as const

const familyOf = (name: string) => `'${name}', sans-serif`
/* Lo guardado viene como `'Inter', sans-serif`; el desplegable trabaja con el
   nombre suelto. */
const nameOf = (family: string) => (family.split(',')[0] ?? '').replace(/'/g, '').trim()

export function BrandSettings({ projectId, token, onBack, onSaved }: {
  projectId: string
  token: string
  onBack: () => void
  /**
   * Se guardó la marca. La marca vive dentro de los datos del proyecto, así que
   * guardarla cambia su versión; quien guarda esos datos por otro camino
   * (`mutateData`) tiene que releerlos para no partir de una versión vieja.
   */
  onSaved?: () => void
}) {
  const { t } = useI18n()
  const [theme, setTheme] = useState<ProjectTheme | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  /* Qué versión del logo se está eligiendo, o nada. Dos artes distintos —claro y
     oscuro— con un solo selector: guardar cuál se pidió es lo que evita un
     segundo estado booleano por cada uno. */
  const [picking, setPicking] = useState<'light' | 'dark' | null>(null)

  useEffect(() => {
    let active = true
    getProjectTheme(projectId, token)
      /* Se normaliza siempre, venga lo que venga: un proyecto que nunca ha
         pasado por el editor no tiene tema, y uno viejo puede no tener los
         componentes que la pantalla necesita para pintarse. */
      .then((row) => { if (active) setTheme(normalizeProjectTheme(row.theme, DEFAULT_PAGE_THEME)) })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : t('brand.loadFailed')) })
    return () => { active = false }
  }, [projectId, token, t])

  const colors = useMemo(() => (theme ? themeColorsToArray(theme) : []), [theme])

  /* Guardar en cuanto se elige, sin botón: es lo que hace el editor con el mismo
     dato, y un «Guardar» aquí dejaría dos marcas distintas según por dónde se
     hubiera entrado. */
  const commit = async (next: ProjectTheme) => {
    setTheme(next)
    setSaving(true)
    setError('')
    try {
      await saveProjectTheme(projectId, next, token)
      onSaved?.()
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2200)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('brand.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const setPalette = (values: string[]) => {
    if (!theme) return
    void commit(normalizeProjectTheme(applyColorsToTheme(theme, values), DEFAULT_PAGE_THEME))
  }

  const setLogo = (patch: Partial<ProjectTheme['brandLogo']>) => {
    if (!theme) return
    void commit({ ...theme, brandLogo: { ...theme.brandLogo, ...patch } })
  }

  const setFonts = (heading: string, body: string) => {
    if (!theme) return
    void commit(normalizeProjectTheme(applyFontsToTheme(theme, familyOf(heading), familyOf(body)), DEFAULT_PAGE_THEME))
  }

  return (
    <div className="dash-project-general-layout">
      <header className="dash-project-general-intro">
        <button type="button" className="dash-secondary-btn dash-settings-back" onClick={onBack}>
          <IconArrowLeft size={14} /> {t('config.back')}
        </button>
        <div className="dash-header">
          <h1>{t('brand.title')}</h1>
          <p>{t('brand.intro')}</p>
        </div>
      </header>

      {error ? <Notice tone="error" title={t('brand.saveFailed')}>{error}</Notice> : null}

      {!theme ? (
        <p className="dash-brand-loading"><IconLoader2 className="dash-spin" size={16} /> {t('brand.loading')}</p>
      ) : (
        <div className="dash-brand">
          <span className="dash-brand-state" aria-live="polite">
            {saving ? <><IconLoader2 className="dash-spin" size={13} /> {t('brand.saving')}</> : saved ? <><IconCheck size={13} /> {t('brand.savedState')}</> : null}
          </span>

          <section className="dash-brand-block">
            <header>
              <span className="dash-brand-icon" aria-hidden="true"><IconPhotoPlus size={15} /></span>
              <div>
                <strong>{t('brand.logo.title')}</strong>
                <em>{t('brand.logo.note')}</em>
              </div>
            </header>

            <div className="dash-brand-logos">
              {([
                ['light', 'brand.logo.light', theme.brandLogo.imageLight],
                /* El oscuro cae al claro cuando falta, que es lo que hace el
                   editor: la mayoría de las marcas sólo tienen uno, y pedir dos
                   para poder guardar uno es pedir de más. */
                ['dark', 'brand.logo.dark', theme.brandLogo.imageDark],
              ] as const).map(([which, labelKey, url]) => (
                <div className={`dash-brand-logo${which === 'dark' ? ' is-dark' : ''}`} key={which}>
                  <span className="dash-brand-legend">{t(labelKey)}</span>
                  <div className="dash-brand-logo-slot">
                    {url ? <img src={url} alt="" /> : <span className="dash-brand-logo-empty">{t('brand.logo.empty')}</span>}
                  </div>
                  <div className="dash-brand-logo-actions">
                    <button type="button" className="dash-secondary-btn" onClick={() => setPicking(which)}>
                      {url ? t('brand.logo.change') : t('brand.logo.add')}
                    </button>
                    {url ? (
                      <button
                        type="button"
                        className="dash-secondary-btn"
                        onClick={() => setLogo(which === 'light' ? { imageLight: '' } : { imageDark: '' })}
                      >
                        {t('brand.logo.remove')}
                      </button>
                    ) : null}
                  </div>
                  {which === 'dark' && !url ? <small>{t('brand.logo.fallback')}</small> : null}
                </div>
              ))}
            </div>

            <label className="dash-offer-field dash-brand-name">
              <span className="dash-offer-field-label">{t('brand.name.label')}</span>
              <input
                type="text"
                maxLength={120}
                placeholder={t('brand.name.placeholder')}
                value={theme.brandLogo.name}
                onChange={(event) => setTheme({ ...theme, brandLogo: { ...theme.brandLogo, name: event.target.value } })}
                onBlur={(event) => { if (event.target.value !== theme.brandLogo.name) setLogo({ name: event.target.value }) }}
              />
              <label className="dash-brand-check">
                <input
                  type="checkbox"
                  checked={theme.brandLogo.showName}
                  onChange={(event) => setLogo({ showName: event.target.checked })}
                />
                <span>{t('brand.name.show')}</span>
              </label>
            </label>
          </section>

          <section className="dash-brand-block">
            <header>
              <span className="dash-brand-icon" aria-hidden="true"><IconPaint size={15} /></span>
              <div>
                <strong>{t('brand.colors.title')}</strong>
                <em>{t('brand.colors.note')}</em>
              </div>
            </header>

            <ul className="dash-brand-tokens">
              {COLOR_TOKENS.map((token_, index) => (
                <li key={token_.id}>
                  <input
                    type="color"
                    aria-label={token_.nameKey ? t(token_.nameKey) : token_.name}
                    value={colors[index] ?? '#000000'}
                    onChange={(event) => {
                      const next = [...colors]
                      next[index] = event.target.value
                      setPalette(next)
                    }}
                  />
                  <span>{token_.nameKey ? t(token_.nameKey) : token_.name}</span>
                  <code>{colors[index]}</code>
                </li>
              ))}
            </ul>

            <div className="dash-brand-palettes">
              <span className="dash-brand-legend">{t('brand.palettes.legend')}</span>
              <div className="dash-brand-palette-row">
                {/* Una muestra de cada grupo, no las doce primeras: ésas eran todas
                    azules y grises, y nunca salía una rosa ni una oscura. */}
                {paletteSampler(15).map((palette, indice) => (
                  <button
                    type="button"
                    key={palette.id}
                    /* Por su posición y no por su nombre: los nombres eran
                       descripciones en español que no se traducen. Un botón de
                       colores sin etiqueta no lo puede usar quien navega con
                       lector de pantalla, así que hace falta decir ALGO. */
                    title={t('panel.paletteN', { n: indice + 1 })}
                    aria-label={t('panel.paletteN', { n: indice + 1 })}
                    /* Por papel, como en el editor: pasarle las ocho posiciones
                       tal cual ponía el fondo donde iba el primario. */
                    onClick={() => setPalette(paletteColorValues(palette))}
                  >
                    {/* Los cinco que se distinguen a este tamaño: fondo,
                        tarjeta, botón, acento y tinta. Los nueve en un botón de
                        cien píxeles serían nueve rayas indistinguibles. */}
                    {[palette.background, palette.card, palette.button, palette.accent, palette.ink].map((color, index) => (
                      <i key={index} style={{ background: color }} />
                    ))}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="dash-brand-block">
            <header>
              <span className="dash-brand-icon" aria-hidden="true"><IconTypography size={15} /></span>
              <div>
                <strong>{t('brand.fonts.title')}</strong>
                <em>{t('brand.fonts.note')}</em>
              </div>
            </header>

            <div className="dash-brand-fonts">
              {([
                ['brand.fonts.headings', nameOf(theme.typography?.headingFont ?? ''), (value: string) => setFonts(value, nameOf(theme.typography?.bodyFont ?? 'Inter'))],
                ['brand.fonts.body', nameOf(theme.typography?.bodyFont ?? ''), (value: string) => setFonts(nameOf(theme.typography?.headingFont ?? 'Inter'), value)],
              ] as const).map(([labelKey, current, onPick]) => (
                <label className="dash-offer-field" key={labelKey}>
                  <span className="dash-offer-field-label">{t(labelKey)}</span>
                  <select value={current || 'Inter'} onChange={(event) => onPick(event.target.value)}>
                    {FONTS.map((font) => <option key={font} value={font}>{font}</option>)}
                  </select>
                  <span className="dash-brand-sample" style={{ fontFamily: familyOf(current || 'Inter') }}>
                    {t('brand.fonts.sample')}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <p className="dash-brand-note">
            {t('brand.editorNote')}
          </p>
        </div>
      )}

      {picking ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onClose={() => setPicking(null)}
          onSelect={(url) => {
            setLogo(picking === 'light' ? { imageLight: url } : { imageDark: url })
            setPicking(null)
          }}
        />
      ) : null}
    </div>
  )
}
